import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Calendar, Users, CheckCircle2, Clock, Video, 
  User, Crown, Sparkles, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WonderTwinDashboardPage = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    wonder_twin_sessions: [],
    group_sessions: [],
    stats: { wonder_twin_scheduled: 0, wonder_twin_completed: 0, group_scheduled: 0, group_completed: 0 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wonder-twin/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async (sessionId, type) => {
    try {
      const endpoint = type === 'wonder_twin' 
        ? `/api/admin/wonder-twin-sessions/${sessionId}`
        : `/api/admin/group-sessions/${sessionId}`;
      
      await axios.put(`${API_URL}${endpoint}`, { status: 'completed' });
      toast.success('Session marked complete');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to update session');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="wonder-twin-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-forest flex items-center gap-3">
            <Crown className="h-8 w-8 text-terracotta" />
            Wonder Twin Dashboard
          </h1>
          <p className="text-gray-600 mt-2">Welcome, {user?.full_name}! Here's your upcoming sessions.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Calendar className="h-6 w-6 text-terracotta mx-auto mb-2" />
              <p className="text-2xl font-bold text-forest">{dashboardData.stats.wonder_twin_scheduled}</p>
              <p className="text-xs text-gray-500">1:1 Sessions Scheduled</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-forest">{dashboardData.stats.wonder_twin_completed}</p>
              <p className="text-xs text-gray-500">1:1 Sessions Completed</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-forest">{dashboardData.stats.group_scheduled}</p>
              <p className="text-xs text-gray-500">Group Sessions Scheduled</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Sparkles className="h-6 w-6 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-forest">{dashboardData.stats.group_completed}</p>
              <p className="text-xs text-gray-500">Group Sessions Completed</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Wonder Twin 1:1 Sessions */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <User className="h-5 w-5 text-terracotta" />
                Upcoming 1:1 Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData.wonder_twin_sessions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No upcoming 1:1 sessions</p>
              ) : (
                <div className="space-y-4">
                  {dashboardData.wonder_twin_sessions.map((session) => (
                    <div key={session.id} className="p-4 border border-sand rounded-xl">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-forest">{session.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              <User className="h-3 w-3 mr-1" />
                              {session.client_name || 'Client'}
                            </Badge>
                            <span className="text-sm text-gray-500">{session.client_email}</span>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {formatDate(session.scheduled_date)} at {session.scheduled_time}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {session.duration_minutes} min
                            </span>
                          </div>
                          {session.zoom_link && (
                            <a 
                              href={session.zoom_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:underline"
                            >
                              <Video className="h-4 w-4" /> Join Zoom
                            </a>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-green-600"
                          onClick={() => handleMarkComplete(session.id, 'wonder_twin')}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Complete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Group Sessions */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                Upcoming Group Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData.group_sessions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No upcoming group sessions</p>
              ) : (
                <div className="space-y-4">
                  {dashboardData.group_sessions.map((session) => (
                    <div key={session.id} className="p-4 border border-sand rounded-xl">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-forest">{session.title}</h3>
                            <Badge variant="outline" className="text-xs">
                              {session.session_type?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{session.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {formatDate(session.scheduled_date)} at {session.scheduled_time}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {session.duration_minutes} min
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {session.assigned_tiers?.map(tier => (
                              <Badge key={tier} className="bg-sand text-forest text-xs">
                                {tier.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                          {session.zoom_link && (
                            <a 
                              href={session.zoom_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:underline"
                            >
                              <Video className="h-4 w-4" /> Join Zoom (pw: {session.zoom_password})
                            </a>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-green-600"
                          onClick={() => handleMarkComplete(session.id, 'group')}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Complete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WonderTwinDashboardPage;
